import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import FiltersDialog from "components/Filters/FiltersDialog";
import { parseWorkOrderStatus } from "utils/enum-parser";

import {
  TAssignedFilters,
  TCanceledFilters,
  TClosedFilters,
  TCompleteFilters,
  TOpenFilters
} from "interfaces/MaintenanceWorkOrdersPageState";
import {
  setAssignedFilters,
  setCanceledFilters,
  setClosedFilters,
  setCompleteFilters,
  setOpenFilters
} from "store/actions/maintenanceWorkOrdersPage";
import FiltersContent from "./FiltersContent";
import { initialSliderFilterValues } from "./filterOptions";
import { getTabsStatuses } from "../helper";
import { useLocalStateFilters } from "../useLocalStateFilters";

interface IProps {
  activeTab: racehorse360.WorkOrderStatus;
  onClose: () => void;
}

const WorkOrdersFiltersDialog = (props: IProps) => {
  const { onClose, activeTab } = props;
  const dispatch = useDispatch();
  const {
    isOpenTab,
    isAssignedTab,
    isCompleteTab,
    isClosedTab,
    isCanceledTab
  } = getTabsStatuses(activeTab);
  const {
    openFiltersState,
    assignedFiltersState,
    completeFiltersState,
    closedFiltersState,
    canceledFiltersState
  } = useLocalStateFilters();
  const {
    openDate,
    setOpenDate,
    openOrderTypes,
    setOpenOrderTypes,
    openReporters,
    setOpenReporters,
    timeOpen,
    setTimeOpen,
    hasSelectedOpenFilters,
    hasChangedSavedOpenValues
  } = openFiltersState;
  const {
    assignedDate,
    setAssignedDate,
    assignedTo,
    setAssignedTo,
    assignedOrderTypes,
    setAssignedOrderTypes,
    assignedReporters,
    setAssignedReporters,
    timeAssigned,
    setTimeAssigned,
    hasSelectedAssignedFilters,
    hasChangedSavedAssignedValues
  } = assignedFiltersState;
  const {
    setCompleteOrderTypes,
    completeOrderTypes,
    setCompleteAssignedTo,
    completeAssignedTo,
    setCompletedDate,
    setCompleteAssignedDate,
    completeAssignedDate,
    completedDate,
    completeTimeAssigned,
    setCompleteTimeAssigned,
    hasSelectedCompleteFilters,
    hasChangedSavedCompleteValues
  } = completeFiltersState;
  const {
    setClosedReporters,
    setClosedOrderTypes,
    closedOrderTypes,
    setClosedAssignedTo,
    closedAssignedTo,
    setClosedDate,
    closedDate,
    closedReporters,
    closedTimeOpen,
    setClosedTimeOpen,
    closedTimeAssigned,
    setClosedTimeAssigned,
    hasChangedSavedClosedValues,
    hasSelectedClosedFilters
  } = closedFiltersState;
  const {
    setCanceledOrderTypes,
    canceledOrderTypes,
    canceledDate,
    setCanceledAssignedTo,
    canceledAssignedTo,
    setCanceledOpenDate,
    canceledOpenDate,
    setCanceledReporters,
    canceledReporters,
    setCanceledDate,
    canceledTimeAssigned,
    setCanceledTimeAssigned,
    hasSelectedCanceledFilters,
    hasChangedSavedCanceledValues
  } = canceledFiltersState;

  const [isEnabledReset, setIsEnabledReset] = useState(false);
  const [isEnabledApply, setIsEnabledApply] = useState<boolean>(false);

  const handleApplyFilters = () => {
    let filters:
      | TOpenFilters
      | TAssignedFilters
      | TCompleteFilters
      | TClosedFilters
      | TCanceledFilters;

    if (isOpenTab) {
      filters = {
        openDate,
        timeOpen,
        orderTypes: openOrderTypes,
        reporters: openReporters
      };

      dispatch(setOpenFilters(filters));
    } else if (isAssignedTab) {
      filters = {
        assignedDate,
        timeAssigned,
        assignedTo: assignedTo,
        orderTypes: assignedOrderTypes,
        reporters: assignedReporters
      };

      dispatch(setAssignedFilters(filters));
    } else if (isCompleteTab) {
      filters = {
        completedDate,
        timeAssigned: completeTimeAssigned,
        assignedDate: completeAssignedDate,
        assignedTo: completeAssignedTo,
        orderTypes: completeOrderTypes
      };

      dispatch(setCompleteFilters(filters));
    } else if (isClosedTab) {
      filters = {
        closedDate,
        timeOpen: closedTimeOpen,
        timeAssigned: closedTimeAssigned,
        reporters: closedReporters,
        assignedTo: closedAssignedTo,
        orderTypes: closedOrderTypes
      };

      dispatch(setClosedFilters(filters));
    } else if (isCanceledTab) {
      filters = {
        canceledDate,
        timeAssigned: canceledTimeAssigned,
        reporters: canceledReporters,
        openDate: canceledOpenDate,
        assignedTo: canceledAssignedTo,
        orderTypes: canceledOrderTypes
      };

      dispatch(setCanceledFilters(filters));
    }

    onClose();
  };

  const handleResetFilters = () => {
    if (isOpenTab) {
      setOpenDate(0);
      setOpenOrderTypes(null);
      setOpenReporters([]);
      setTimeOpen(initialSliderFilterValues);
    } else if (isAssignedTab) {
      setAssignedDate(0);
      setAssignedTo(null);
      setAssignedOrderTypes(null);
      setAssignedReporters([]);
      setTimeAssigned(initialSliderFilterValues);
    } else if (isCompleteTab) {
      setCompleteAssignedDate(0);
      setCompletedDate(0);
      setCompleteAssignedTo(null);
      setCompleteOrderTypes(null);
      setCompleteTimeAssigned(initialSliderFilterValues);
    } else if (isClosedTab) {
      setClosedDate(0);
      setClosedAssignedTo(null);
      setClosedOrderTypes(null);
      setClosedReporters([]);
      setClosedTimeOpen(initialSliderFilterValues);
      setClosedTimeAssigned(initialSliderFilterValues);
    } else if (isCanceledTab) {
      setCanceledReporters([]);
      setCanceledOpenDate(0);
      setCanceledAssignedTo(null);
      setCanceledDate(0);
      setCanceledOrderTypes(null);
      setCanceledTimeAssigned(initialSliderFilterValues);
    }
  };

  useEffect(() => {
    isOpenTab && setIsEnabledReset(hasSelectedOpenFilters);
    isAssignedTab && setIsEnabledReset(hasSelectedAssignedFilters);
    isCompleteTab && setIsEnabledReset(hasSelectedCompleteFilters);
    isClosedTab && setIsEnabledReset(hasSelectedClosedFilters);
    isCanceledTab && setIsEnabledReset(hasSelectedCanceledFilters);
  }, [
    hasSelectedOpenFilters,
    hasSelectedAssignedFilters,
    hasSelectedCompleteFilters,
    hasSelectedClosedFilters,
    hasSelectedCanceledFilters
  ]);

  useEffect(() => {
    isOpenTab && setIsEnabledApply(hasChangedSavedOpenValues);
    isAssignedTab && setIsEnabledApply(hasChangedSavedAssignedValues);
    isCompleteTab && setIsEnabledApply(hasChangedSavedCompleteValues);
    isClosedTab && setIsEnabledApply(hasChangedSavedClosedValues);
    isCanceledTab && setIsEnabledApply(hasChangedSavedCanceledValues);
  }, [
    hasChangedSavedOpenValues,
    hasChangedSavedAssignedValues,
    hasChangedSavedCompleteValues,
    hasChangedSavedClosedValues,
    hasChangedSavedCanceledValues
  ]);

  return (
    <FiltersDialog
      onClose={onClose}
      title={`${parseWorkOrderStatus(activeTab)} Work Orders Filter`}
      disabledApplyButton={!isEnabledApply}
      disabledResetButton={!isEnabledReset}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
      minHeightDialog={553}
    >
      <FiltersContent
        activeTab={activeTab}
        openFiltersState={openFiltersState}
        assignedFiltersState={assignedFiltersState}
        completeFiltersState={completeFiltersState}
        closedFiltersState={closedFiltersState}
        canceledFiltersState={canceledFiltersState}
      />
    </FiltersDialog>
  );
};

export default WorkOrdersFiltersDialog;
